import { ApiLeadStatusRule, ApiLeadStatusType } from "@incendium/api";
import Add from "@mui/icons-material/Add";
import { Delete, Edit, FilterAlt } from "@mui/icons-material";
import { Typography, CardActions, Button, IconButton } from "@mui/material";
import { Box } from "@mui/system";
import { leadStatusService } from "Apis";
import CreateNameDialog from "Components/CreateNameDialog/CreateNameDialog";
import GlassCard from "Components/GlassCard/GlassCard";
import { CenterPage } from "Components/CenterPage/CenterPage";
import { useNotification, useSelectedProject } from "Hooks";
import { useConfirmation } from "Hooks/useConfirmation";
import { useLeadStatusRules } from "Hooks/useLeadStatusRules";
import produce from "immer";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";

const StageCardWrapper = styled(Box)(({ theme }) => ({
  width: 240,
  flex: "0 0 240px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "stretch",
}));

const AddStageIconButton = styled(IconButton)(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: 999,
  padding: 0,
  boxShadow: "none",
  background: "rgba(201, 211, 244, 0.08)",
  border: "1px solid rgba(201, 211, 244, 0.5)",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    background: "rgba(201, 211, 244, 0.14)",
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    boxShadow: "none",
    transform: "rotate(90deg)",
  },
}));

function LeadsPage() {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [pendingInsertIndex, setPendingInsertIndex] = useState(0);
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { selectedProject: project } = useSelectedProject();
  const handleDelete = useConfirmation();
  const { leadStatusRules, setLeadStatusRules } = useLeadStatusRules();
  const { pathByName } = useProtectedRoutes();

  const orderedStages = useMemo(() => {
    return [...(leadStatusRules || [])].sort(
      (a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0),
    );
  }, [leadStatusRules]);

  const onSave = async (name: string) => {
    if (!project) return;
    try {
      const projectId = project.id as number;
      const clientId = project.clientId;

      const insertIndex = pendingInsertIndex;
      const existing = orderedStages;

      const maxStageOrder = existing.reduce(
        (m, s) => Math.max(m, s.stageOrder ?? 0),
        0,
      );
      const shouldShift = insertIndex < existing.length;
      const newStageOrder = shouldShift ? insertIndex + 1 : maxStageOrder + 1;

      const res = await leadStatusService.leadStatusServiceCreateLeadStatusRule(
        {
          projectId,
          payload: {
            name,
            type: ApiLeadStatusType.PROSPECT,
            asDefault: false,
            stageOrder: newStageOrder,
          },
        },
      );

      if (shouldShift) {
        await Promise.all(
          existing.map(async (s, i) => {
            const desiredOrder = i < insertIndex ? i + 1 : i + 2;
            await leadStatusService.leadStatusServiceUpdateLeadStatusRule({
              projectId,
              leadStatusRuleId: s.id as number,
              payload: {
                name: s.name ?? "",
                type: s.type ?? ApiLeadStatusType.PROSPECT,
                asDefault: s.asDefault ?? false,
                stageOrder: desiredOrder,
              },
            });
          }),
        );
      }

      setLeadStatusRules(
        produce(leadStatusRules, (draft) => {
          for (let i = 0; i < existing.length; i++) {
            const s = existing[i];
            const idx = draft.findIndex((d) => d.id === s.id);
            if (idx === -1) continue;
            if (shouldShift) {
              const desiredOrder = i < insertIndex ? i + 1 : i + 2;
              draft[idx].stageOrder = desiredOrder;
            }
          }
          draft.push(res);
        }),
      );

      showSuccessNotification(`Stage (${name}) Created, redirecting`);

      history.push(
        pathByName(RouteNames.LEAD_STATUS, {
          clientId,
          projectId: project.id,
          leadStatusRuleId: res.id,
        }),
      );
    } catch (error) {
      showErrorNotification("Failed to create stage, please try again");
    }
  };

  const openCreateAt = (index: number) => {
    setPendingInsertIndex(index);
    setOpen(true);
  };

  const onEdit = (status: ApiLeadStatusRule) => {
    if (!project) return;
    history.push(
      pathByName(RouteNames.LEAD_STATUS, {
        clientId: project.clientId,
        projectId: project.id,
        leadStatusRuleId: status.id,
      }),
    );
  };

  const onDelete = (status: ApiLeadStatusRule) => {
    if (!project) return;
    handleDelete({
      title: `Are you sure you want to delete this status`,
      body: "This action can not be undone",
      callback: async () => {
        await leadStatusService.leadStatusServiceDeleteLeadStatusRule({
          projectId: project.id as number,
          leadStatusRuleId: status.id as number,
        });

        setLeadStatusRules(
          produce(leadStatusRules, (draft) => {
            const idx = leadStatusRules.findIndex((s) => status.id === s.id);
            if (idx !== -1) draft.splice(idx, 1);
          }),
        );

        return "Status Rule Deleted";
      },
    });
  };

  return (
    <CenterPage sm={12} calcHeight={true}>
      <Box sx={{ width: "100%" }} p={8}>
        <Box mt={3} sx={{ width: "100%" }}>
          {orderedStages.length === 0 ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <GlassCard
                boxProps={{
                  px: { xs: 3, sm: 5 },
                  py: 4,
                  sx: { width: 800, maxWidth: "100%" },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(201, 211, 244, 0.25)",
                    }}
                  >
                    <FilterAlt />
                  </Box>

                  <Typography variant="h4" fontWeight={600}>
                    Set up your funnel
                  </Typography>

                  <Typography variant="body1" color="text.secondary">
                    Create ordered funnel stages and define the rules for when a
                    visitor should move through."
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => openCreateAt(0)}
                    startIcon={<Add />}
                  >
                    Create Funnel Stage
                  </Button>
                </Box>
              </GlassCard>
            </Box>
          ) : (
            <GlassCard boxProps={{ px: 3, py: 5, sx: { width: "100%" } }}>
              <Box mb={3}>
                <Typography variant="h3" fontWeight={600}>
                  Your Funnel Stages
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Edit your funnel and modify the rules that control how
                  visitors move through.
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  overflowX: "auto",
                  pb: 2,
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <AddStageIconButton
                    onClick={() => openCreateAt(0)}
                    aria-label="Add stage"
                  >
                    <Add />
                  </AddStageIconButton>
                </Box>

                {orderedStages.map((stage, i) => (
                  <Box key={stage.id} sx={{ display: "contents" }}>
                    <StageCardWrapper>
                      <GlassCard sx={{ width: "100%" }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ minHeight: 34 }}
                        >
                          <Typography color="text.secondary">
                            Stage {i + 1}
                          </Typography>
                        </Box>
                        <Typography variant="h3" mt={1} mb={2}>
                          {stage.name || `Stage ${i + 1}`}
                        </Typography>

                        <CardActions>
                          <Button
                            onClick={() => onEdit(stage)}
                            fullWidth
                            size="small"
                            startIcon={<Edit />}
                            color="primary"
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => onDelete(stage)}
                            fullWidth
                            size="small"
                            startIcon={<Delete />}
                            color="secondary"
                          >
                            Delete
                          </Button>
                        </CardActions>
                      </GlassCard>
                    </StageCardWrapper>

                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <AddStageIconButton
                        onClick={() => openCreateAt(i + 1)}
                        aria-label="Add stage"
                      >
                        <Add />
                      </AddStageIconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            </GlassCard>
          )}
        </Box>

        <CreateNameDialog
          open={open}
          setOpen={setOpen}
          onSaved={onSave}
          title="Add Lead Funnel Stage"
        />
      </Box>
    </CenterPage>
  );
}

export default LeadsPage;
